import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Box, Typography } from "@mui/material";
import * as echarts from "echarts";
import { GenericDonutChartProps } from "../../type";
import { NO_DATA_MESSAGE } from "../../../../utils/consts";
import {
  genericDonutChartConfig,
  genericDonutChartStyles,
  genericDonutDefaultColors,
  genericDonutLabelLineStyle,
  genericDonutLabelStyle,
  genericDonutSliceItemStyle,
  genericDonutTooltipTextStyle,
  getGenericDonutTitleSx,
} from "../L2Widgets/styles/genericDonutChartStyles";

export const GenericDonutChart: React.FC<GenericDonutChartProps> = ({
  title,
  titleAlign = "center",
  data,
  loading = false,
  height = 400,
  minHeight = 400,
  emptyMessage = NO_DATA_MESSAGE,
  tooltipValueFormatter,
  resizeTrigger,
  innerRadius = genericDonutChartConfig.innerRadius,
  outerRadius = genericDonutChartConfig.outerRadius,
  showLabels = true,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const resizeRafRef = useRef<number | null>(null);
  const chartData = useMemo(
    () => data.filter((row) => row.value > 0),
    [data],
  );
  const hasData = chartData.length > 0;

  const scheduleResize = useCallback(() => {
    if (resizeRafRef.current !== null) {
      cancelAnimationFrame(resizeRafRef.current);
    }
    resizeRafRef.current = requestAnimationFrame(() => {
      resizeRafRef.current = null;
      chartInstance.current?.resize();
    });
  }, []);

  const defaultTooltipValueFormatter = useCallback(
    (value: number, percent: number) =>
      `${value.toLocaleString("en-US")} (${percent.toFixed(1)}%)`,
    [],
  );

  const option = useMemo(() => {
    const formatTooltipValue =
      tooltipValueFormatter ?? defaultTooltipValueFormatter;

    return {
      tooltip: {
        trigger: "item",
        appendToBody: true,
        confine: false,
        extraCssText: "z-index: 10000;",
        textStyle: genericDonutTooltipTextStyle,
        formatter: (params: {
          name?: string;
          value?: number;
          percent?: number;
        }) => {
          const name = params.name ?? "";
          const value = Number(params.value ?? 0);
          const percent = Number(params.percent ?? 0);
          return `${name}: ${formatTooltipValue(value, percent, name)}`;
        },
      },
      series: [
        {
          type: "pie",
          radius: [innerRadius, outerRadius],
          center: ["50%", "50%"],
          avoidLabelOverlap: true,
          itemStyle: genericDonutSliceItemStyle,
          label: {
            ...genericDonutLabelStyle,
            show: showLabels,
          },
          labelLine: {
            ...genericDonutLabelLineStyle,
            show: showLabels,
          },
          data: chartData.map((row, index) => ({
            name: row.label,
            value: row.value,
            itemStyle: {
              color:
                row.color ??
                genericDonutDefaultColors[
                  index % genericDonutDefaultColors.length
                ],
            },
          })),
        },
      ],
    };
  }, [
    chartData,
    defaultTooltipValueFormatter,
    innerRadius,
    outerRadius,
    showLabels,
    tooltipValueFormatter,
  ]);

  useEffect(() => {
    if (!hasData) {
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
      chartInstance.current?.dispose();
      chartInstance.current = null;
      return undefined;
    }

    const dom = chartRef.current;
    if (!dom) {
      return undefined;
    }

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(dom);
      const ro = new ResizeObserver(() => {
        scheduleResize();
      });
      ro.observe(dom);
      resizeObserverRef.current = ro;
      globalThis.addEventListener("resize", scheduleResize);
    }

    return () => {
      if (resizeRafRef.current !== null) {
        cancelAnimationFrame(resizeRafRef.current);
        resizeRafRef.current = null;
      }
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
      globalThis.removeEventListener("resize", scheduleResize);
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, [hasData, scheduleResize]);

  useEffect(() => {
    if (!hasData || !chartInstance.current) {
      return;
    }
    chartInstance.current.setOption(option, {
      notMerge: true,
      lazyUpdate: true,
    });
    scheduleResize();
  }, [hasData, option, scheduleResize]);

  useEffect(() => {
    if (resizeTrigger === undefined) return;
    const timer = globalThis.setTimeout(() => scheduleResize(), 350);
    return () => globalThis.clearTimeout(timer);
  }, [resizeTrigger, scheduleResize]);

  if (!hasData && !loading) {
    return (
      <Box>
        {title ? (
          <Typography sx={getGenericDonutTitleSx(titleAlign)}>
            {title}
          </Typography>
        ) : null}
        <Box sx={genericDonutChartStyles.emptyStateContainer}>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {emptyMessage}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={genericDonutChartStyles.root}>
      {title ? (
        <Typography sx={getGenericDonutTitleSx(titleAlign)}>
          {title}
        </Typography>
      ) : null}
      <Box sx={genericDonutChartStyles.chartWrapper(minHeight, loading, hasData)}>
        {hasData ? (
          <Box
            ref={chartRef}
            sx={genericDonutChartStyles.chartCanvas(height)}
          />
        ) : (
          <Box sx={genericDonutChartStyles.chartPlaceholder(minHeight)} />
        )}
      </Box>
    </Box>
  );
};
